import ShowMoreButton from "@/components/company/shared/show-more-button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type ToggleableBadgeListProps<T> = {
  items: readonly T[];
  /** Maximum items shown collapsed. Items beyond this threshold are hidden behind a toggle. */
  initialDisplay?: number;
  getKey: (item: T, index: number) => string;
  renderBadge?: (item: T) => ReactNode;
};

/**
 * Flex-wrapped list of badges with a "Show more (+N)" / "Show less" toggle
 * when the list exceeds `initialDisplay`. Covers the pattern used by
 * technologies, market categories and similar tag clouds.
 */
export default function ToggleableBadgeList<T>({
  items,
  initialDisplay = 18,
  getKey,
  renderBadge,
}: ToggleableBadgeListProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [fullHeight, setFullHeight] = useState<number | null>(null);

  const hiddenCount = Math.max(0, items.length - initialDisplay);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const measure = () => {
      const full = element.scrollHeight;
      setFullHeight(full);

      if (items.length <= initialDisplay) {
        setCollapsedHeight(full);
        return;
      }

      const lastVisibleChild = element.children[initialDisplay - 1] as
        | HTMLElement
        | undefined;
      if (!lastVisibleChild) return;

      const containerTop = element.getBoundingClientRect().top;
      const childBottom = lastVisibleChild.getBoundingClientRect().bottom;
      setCollapsedHeight(childBottom - containerTop);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [items.length, initialDisplay]);

  if (items.length === 0) return null;

  const targetHeight = isExpanded ? fullHeight : collapsedHeight;
  const animateHeight: number | "auto" = targetHeight ?? "auto";

  return (
    <div className="flex flex-col gap-3">
      <motion.div
        initial={false}
        animate={{ height: animateHeight }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{ overflow: "hidden" }}
      >
        <div ref={contentRef} className="flex flex-wrap gap-1.5">
          {items.map((item, index) => (
            <Badge key={getKey(item, index)} variant="secondary">
              {renderBadge ? renderBadge(item) : (item as unknown as ReactNode)}
            </Badge>
          ))}
        </div>
      </motion.div>
      {hiddenCount > 0 && (
        <ShowMoreButton
          isExpanded={isExpanded}
          hiddenCount={hiddenCount}
          onToggle={() => setIsExpanded((v) => !v)}
        />
      )}
    </div>
  );
}
